import type {
  AppliedReduction,
  Price,
  Product,
  ProductImage,
  Variant,
} from '@scayle/storefront-nuxt'
import { DIRECT_ORDER_ATTRIBUTE_KEY } from '../constants/search'
import { sustainabilityAttributes } from './sustainability'

const cardAttributeKeys = [
  'colorRange',
  'colorHex',
  'color',
  'brand',
  'name',
  'premiumsegment',
  'einfuehrungsgrund',
  DIRECT_ORDER_ATTRIBUTE_KEY,
  ...sustainabilityAttributes,
]

const imageAttributeKeys = [
  'assetType',
  'imageType',
  'imageView',
  'imageBackground',
  'imageKind',
  'image1stView',
]

const variantAttributeKeys = ['sizeCategory']
const cardImageAssetTypes = new Set(['_f', '_5', '_kb', '_1'])

export const shapeProductCardPayload = (product: Product): Product => {
  const shaped = {
    id: product.id,
    masterKey: product.masterKey,
    referenceKey: product.referenceKey,
    isActive: product.isActive,
    isSoldOut: product.isSoldOut,
    isNew: product.isNew,
    attributes: pickRecord(product.attributes, cardAttributeKeys),
    variants: shapeCardVariants(product.variants),
    siblings: product.siblings?.map(shapeCardSibling),
    images: shapeCardImages(product.images),
    categories: shapeCardCategories(product.categories),
    lowestPriorPrice: shapeLowestPriorPrice(product.lowestPriorPrice),
  } satisfies Partial<Product>

  return shaped as Product
}

export const shapeProductCardPayloads = (products: Product[] = []): Product[] =>
  products.map(shapeProductCardPayload)

export const shapeProductCarouselPayload = (product: Product): Product => {
  const shaped = shapeProductCardPayload(product)
  delete shaped.siblings

  return shaped
}

export const shapeProductCarouselPayloads = (
  products: Product[] = [],
): Product[] => products.map(shapeProductCarouselPayload)

const shapeCardSibling = (sibling: Product): Product =>
  ({
    id: sibling.id,
    isActive: sibling.isActive,
    attributes: pickRecord(sibling.attributes, [
      'colorRange',
      'colorHex',
      'color',
      DIRECT_ORDER_ATTRIBUTE_KEY,
    ]),
    images: shapeCardImages(sibling.images),
  }) as unknown as Product

const shapeCardVariants = (variants: Variant[] = []): Variant[] => {
  const seenSizeCategories = new Set<string>()
  const lowestPriceVariant = getLowestPriceVariant(variants)

  return variants.flatMap((variant, index) => {
    const sizeCategory = getAttributeValue(variant.attributes, 'sizeCategory')
    const includeSizeCategory =
      sizeCategory !== '' && !seenSizeCategories.has(sizeCategory)
    const includeProductData = index === 0 || variant === lowestPriceVariant

    if (sizeCategory) {
      seenSizeCategories.add(sizeCategory)
    }

    if (!includeProductData && !includeSizeCategory) {
      return []
    }

    return [
      shapeCardVariant(variant, {
        includeProductData,
        includePrice: true,
        includeSizeCategory,
      }),
    ]
  })
}

const shapeCardVariant = (
  variant: Variant,
  options: {
    includeProductData: boolean
    includePrice: boolean
    includeSizeCategory: boolean
  },
): Variant =>
  ({
    ...(options.includeProductData && {
      id: variant.id,
      referenceKey: variant.referenceKey,
      isActive: (variant as unknown as Record<string, unknown>).isActive,
      isSoldOut: (variant as unknown as Record<string, unknown>).isSoldOut,
    }),
    stock: shapeCardStock(variant.stock),
    ...(options.includePrice && {
      price: shapeCardPrice(variant.price),
    }),
    ...(options.includeSizeCategory && {
      attributes: pickRecord(variant.attributes, variantAttributeKeys),
    }),
  }) as unknown as Variant

const getLowestPriceVariant = (variants: Variant[] = []): Variant | undefined =>
  variants.reduce((lowest: Variant | undefined, variant) => {
    if (!isCardVariantInStock(variant) || !variant.price) {
      return lowest
    }

    if (!lowest?.price) {
      return variant
    }

    const lowestPrice = lowest.price.withoutTax ?? lowest.price.withTax
    const variantPrice = variant.price.withoutTax ?? variant.price.withTax

    return lowestPrice <= variantPrice ? lowest : variant
  }, undefined)

const isCardVariantInStock = (variant: Variant): boolean =>
  Boolean(
    variant.stock &&
    (variant.stock.quantity > 0 || variant.stock.isSellableWithoutStock),
  )

const shapeCardImages = (images: ProductImage[] = []): ProductImage[] => {
  const cardImages = images.filter((image) =>
    cardImageAssetTypes.has(getAttributeValue(image.attributes, 'assetType')),
  )

  return (cardImages.length ? cardImages : images.slice(0, 2)).map(
    (image) =>
      ({
        hash: image.hash,
        attributes: pickRecord(image.attributes, imageAttributeKeys),
      }) as ProductImage,
  )
}

const shapeCardCategories = (categoryPaths: any[][] = []): any[][] =>
  categoryPaths.map((categoryPath) =>
    categoryPath.map((category) => ({
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      categorySlug: category.categorySlug,
      categoryUrl: category.categoryUrl,
    })),
  )

const shapeCardPrice = (price?: Price): Price | undefined =>
  price &&
  ({
    withTax: price.withTax,
    appliedReductions: price.appliedReductions?.map(shapeAppliedReduction),
  } as Price)

const shapeAppliedReduction = (reduction: AppliedReduction): AppliedReduction =>
  ({
    category: reduction.category,
    type: reduction.type,
    amount: {
      relative: reduction.amount.relative,
      absoluteWithTax: reduction.amount.absoluteWithTax,
    },
  }) as AppliedReduction

const shapeLowestPriorPrice = (lowestPriorPrice: any) =>
  lowestPriorPrice && {
    withTax: lowestPriorPrice.withTax,
    relativeDifferenceToPrice: lowestPriorPrice.relativeDifferenceToPrice,
  }

const shapeCardStock = (stock: any) =>
  stock && {
    quantity: stock.quantity,
    isSellableWithoutStock: stock.isSellableWithoutStock,
  }

const pickRecord = <T extends Record<string, unknown> | undefined>(
  record: T,
  keys: string[],
): T => {
  if (!record) {
    return record
  }

  return Object.fromEntries(
    keys
      .filter((key) => record[key] !== undefined)
      .map((key) => [key, shapeAttribute(record[key], key)]),
  ) as T
}

const shapeAttribute = (attribute: any, key: string) => ({
  ...(attribute?.multiSelect && { multiSelect: true }),
  values: Array.isArray(attribute?.values)
    ? attribute.values.map((value: any) => shapeAttributeValue(value, key))
    : shapeAttributeValue(attribute?.values, key),
})

// Color values need their id downstream: ColorCircle renders it as
// data-color-id (scanned by the HTF product-colors script) and resolves the
// swatch background / multi-color gradient via getColorCodeForId(id).
const idPreservingAttributeKeys = new Set([
  'color',
  'colorHex',
  'colorRange',
  DIRECT_ORDER_ATTRIBUTE_KEY,
])

const shapeAttributeValue = (value: any, key: string) =>
  value && {
    label: value.label,
    value: value.value,
    ...(idPreservingAttributeKeys.has(key) && { id: value.id }),
  }

const getAttributeValue = (
  attributes: Record<string, any> | undefined,
  key: string,
): string => {
  const values = attributes?.[key]?.values
  const value = Array.isArray(values) ? values[0] : values

  return value?.value?.toString() ?? value?.label?.toString() ?? ''
}
